import { useEffect, useState } from "react";
import api from "@/lib/api";
import { ACard, inputDark } from "@/components/shared/AdminKit";
import { useTr, fmtDate } from "@/lib/tr";
import { Loader2, ChevronLeft, ChevronRight } from "lucide-react";

const LABELS = {
  login: ["Connexion", "Login"], login_success: ["Connexion réussie", "Login success"], logout: ["Déconnexion", "Logout"],
  login_failure: ["Échec de connexion", "Login failure"], login_blocked_status: ["Connexion bloquée (statut)", "Login blocked (status)"],
  login_2fa_required: ["Code de connexion envoyé", "Login code sent"], "2fa_failure": ["Code invalide", "Invalid code"],
  access_denied: ["Accès refusé", "Access denied"], agent_create: ["Compte créé", "Account created"], agent_update: ["Profil modifié", "Profile updated"],
  agent_permissions_change: ["Permissions modifiées", "Permissions changed"], agent_languages_change: ["Langues modifiées", "Languages changed"],
  agent_password_reset: ["Mot de passe réinitialisé", "Password reset"], password_change: ["Mot de passe changé", "Password changed"],
  password_changed: ["Mot de passe changé", "Password changed"], agent_suspend: ["Suspension", "Suspended"], agent_reactivate: ["Réactivation", "Reactivated"],
  agent_disable: ["Désactivation", "Disabled"], agent_revoke_sessions: ["Sessions révoquées", "Sessions revoked"], agent_sessions_revoked: ["Sessions révoquées", "Sessions revoked"],
  agent_email_verified: ["E-mail vérifié", "Email verified"], agent_email_changed: ["E-mail modifié", "Email changed"],
  service_create: ["Service créé", "Service created"], service_update: ["Service modifié", "Service updated"], service_price_change: ["Prix modifié", "Price changed"],
  service_quantity_change: ["Quantités modifiées", "Quantities changed"], service_bulk_publish: ["Publication / dépublication", "Publish / unpublish"],
  service_archive: ["Service archivé", "Service archived"], order_status_change: ["Statut de commande", "Order status"], order_retry: ["Commande relancée", "Order retried"],
  ticket_reply: ["Réponse ticket", "Ticket reply"], ticket_status: ["Statut ticket", "Ticket status"], ticket_transfer: ["Transfert ticket", "Ticket transfer"],
};

const detailText = (d) => {
  const keys = Object.keys(d || {}).filter((k) => !["result"].includes(k));
  if (!keys.length) return "";
  return keys.map((k) => `${k}: ${typeof d[k] === "object" ? JSON.stringify(d[k]) : d[k]}`).join(" · ").slice(0, 300);
};

export function AgentActivity({ agentId }) {
  const tr = useTr();
  const [kind, setKind] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get(`/admin/agents/${agentId}/activity`, { params: { page, page_size: 30, kind: kind || undefined } }).then((r) => setData(r.data)).catch(() => setData({ items: [], total: 0 }));
  }, [agentId, page, kind]);
  const pages = data ? Math.max(1, Math.ceil(data.total / 30)) : 1;
  return (
    <ACard>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800 p-4">
        <select value={kind} onChange={(e) => { setKind(e.target.value); setPage(1); }} className={inputDark + " sm:!w-auto"} data-testid="activity-filter">
          <option value="">{tr("Toutes les activités", "All activity")}</option>
          <option value="actions">{tr("Actions", "Actions")}</option>
          <option value="security">{tr("Connexions et sécurité", "Logins & security")}</option>
        </select>
        <span className="text-xs text-slate-500">{data?.total ?? 0} {tr("événements", "events")}</span>
      </div>
      {!data ? <div className="py-12 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-accent" /></div> : !data.items.length ? (
        <div className="py-12 text-center text-sm text-slate-500" data-testid="activity-empty">{tr("Aucune activité enregistrée.", "No activity recorded.")}</div>
      ) : (
        <div className="divide-y divide-slate-800" data-testid="activity-list">
          {data.items.map((e, i) => (
            <div key={i} className="grid gap-1 px-4 py-3 text-sm sm:grid-cols-[170px_1fr_auto]" data-testid={`activity-${i}`}>
              <div className="text-xs text-slate-500">{fmtDate(e.at, tr.lang)}</div>
              <div>
                <span className="font-medium text-white">{(LABELS[e.action] || [e.action, e.action])[tr.lang === "en" ? 1 : 0]}</span>
                {e.entity && e.entity !== "auth" && <span className="text-slate-500"> · {e.entity}{e.entity_id && e.entity_id !== agentId ? ` ${String(e.entity_id).slice(0, 8)}` : ""}</span>}
                {!e.by_self && e.actor && <span className="text-xs text-slate-500"> · {tr("par", "by")} {e.actor}</span>}
                {detailText(e.details) && <div className="mt-0.5 break-all text-xs text-slate-500">{detailText(e.details)}</div>}
              </div>
              <span className={`h-fit rounded-full px-2 py-0.5 text-[11px] ${e.result === "denied" ? "bg-red-500/15 text-red-400" : "bg-emerald-500/15 text-emerald-400"}`}>{e.result === "denied" ? tr("Refusé", "Denied") : tr("Réussi", "Success")}</span>
            </div>
          ))}
        </div>
      )}
      {data && data.total > 30 && (
        <div className="flex items-center justify-end gap-3 border-t border-slate-800 p-3 text-sm text-slate-400">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded-lg border border-slate-700 p-1.5 disabled:opacity-40" data-testid="activity-prev"><ChevronLeft className="h-4 w-4" /></button>
          {page} / {pages}
          <button disabled={page >= pages} onClick={() => setPage(page + 1)} className="rounded-lg border border-slate-700 p-1.5 disabled:opacity-40" data-testid="activity-next"><ChevronRight className="h-4 w-4" /></button>
        </div>
      )}
    </ACard>
  );
}
